import React from "react";
import { motion } from "framer-motion";
import { useHome } from "@/i18n/home";
import { UwaBadge } from "@/components/UwaBadge";
import { scrollToId } from "@/hooks/useLenis";
import { ArrowRight, ArrowDown, MessageCircle, ShieldCheck, Wifi, Building2, Cctv, Satellite } from "lucide-react";
import { CONTACT } from "@/i18n/translations";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  visible: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: d, ease: [0.16, 1, 0.3, 1] } }),
};

const BADGES = [{ icon: ShieldCheck }, { icon: Wifi }, { icon: Building2 }];

const SPECIALTIES = [
  { key: "wifi", icon: Wifi, main: true },
  { key: "cctv", icon: Cctv },
  { key: "tv", icon: Satellite },
];

export const Hero = () => {
  const h = useHome().hero;
  return (
    <section id="hero" className="relative min-h-[100svh] overflow-hidden" data-testid="hero">
      {/* Background video */}
      {/* Static background image (first frame – no looping video) */}
      <div
        className="absolute inset-0 h-full w-full bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-poster.jpg')" }}
        data-testid="hero-video"
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(9,10,12,0.72) 0%, rgba(9,10,12,0.55) 40%, rgba(9,10,12,0.78) 100%)" }} />

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-5 sm:px-8 pt-32 pb-20">
        {/* Partner badges */}
        <motion.div variants={fadeUp} custom={0.1} initial="hidden" animate="visible" className="mb-8 flex flex-wrap gap-2.5" data-testid="hero-badges">
          {BADGES.map((b, i) => (
            <span key={i} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.08] px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
              <b.icon size={14} color="var(--pt-blue)" /> {h.badges[i]}
            </span>
          ))}
        </motion.div>

        <motion.h1 variants={fadeUp} custom={0.25} initial="hidden" animate="visible" className="max-w-5xl font-display font-semibold uppercase tracking-tight text-white text-[clamp(1.85rem,9vw,2.25rem)] sm:text-6xl lg:text-7xl leading-[1.04]" data-testid="hero-title">
          Smart Solutions
          <br />
          for a{" "}
          <span className="relative inline-block">
            <span className="relative z-10">Connected</span>
            <svg className="pointer-events-none absolute left-1/2 top-1/2 h-[150%] w-[118%] -translate-x-1/2 -translate-y-1/2" viewBox="0 0 200 70" preserveAspectRatio="none" aria-hidden="true">
              <path d="M14,36 C14,14 62,8 102,8 C156,8 190,16 190,36 C190,58 142,62 100,62 C48,62 14,58 14,36" fill="none" stroke="#FF7A00" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </span>
          <br />
          <span style={{ background: "linear-gradient(90deg,#00C3FF,#7FE1FF)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
            World.
          </span>
        </motion.h1>

        <motion.p variants={fadeUp} custom={0.45} initial="hidden" animate="visible" className="mt-7 max-w-xl text-lg sm:text-xl leading-relaxed text-white/80" data-testid="hero-subline">
          <span className="font-semibold text-white">{h.subStrong}</span> {h.sub}
        </motion.p>

        <motion.div variants={fadeUp} custom={0.55} initial="hidden" animate="visible" className="mt-8 flex flex-wrap gap-3" data-testid="hero-specialties">
          {SPECIALTIES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => scrollToId("services")}
              data-testid={`hero-specialty-${s.key}`}
              className={`inline-flex items-center gap-3 rounded-2xl border px-4 py-3 text-left backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 ${s.main ? "border-[#00C3FF]/60 bg-[#00C3FF]/15" : "border-white/15 bg-white/[0.07]"}`}
            >
              <span className={`grid h-10 w-10 place-items-center rounded-xl ${s.main ? "bg-[#00C3FF] text-[#090A0C]" : "bg-white/10 text-[#00C3FF]"}`}>
                <s.icon size={20} strokeWidth={2.2} />
              </span>
              <span>
                <span className="block text-sm font-bold text-white">{h.specialties[i].label}</span>
                <span className={`block text-[11px] uppercase tracking-[0.16em] ${s.main ? "text-[#7FE1FF]" : "text-white/55"}`}>{h.specialties[i].tag}</span>
              </span>
            </button>
          ))}
        </motion.div>

        <motion.div variants={fadeUp} custom={0.7} initial="hidden" animate="visible" className="mt-9 flex flex-wrap items-center gap-3">
          <a
            href={`https://wa.me/${CONTACT.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-[#090A0C] transition-transform hover:scale-[1.03]"
            style={{ background: "var(--pt-blue)" }}
          >
            <MessageCircle size={17} /> {h.cta}
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </a>
          <button onClick={() => scrollToId("services")} data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-[#00C3FF] hover:text-[#00C3FF]">
            {h.cta2}
          </button>
          <span className="text-sm text-white/60">{h.note}</span>
        </motion.div>
        <UwaBadge compact />
        <UwaBadge />
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
        <ArrowDown size={20} className="animate-bounce text-white/50" />
      </motion.div>
    </section>
  );
};
